/**
 * Zuma Chatbot Block
 *
 * Injection block that loads the Zuma AI leasing chatbot.
 * Zuma resolves the property by the page URL — no per-property token needed.
 */

import { useEffect, useRef } from 'react';
import type { BlockDefinition, BlockProps } from '../../types';
import type { ComponentContentSchema } from '@vibesites/runtime';
import { useBlockToken } from '@vibesites/runtime/react';

const SCRIPT_ID = 'zuma-chatbot-script';

function ZumaChatbotComponent(_props: BlockProps) {
  const position = useBlockToken('position', { defaultValue: 'left' });
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;

    const existing = document.getElementById(SCRIPT_ID);
    if (existing) existing.remove();

    const fullUrl = encodeURIComponent(window.location.href);
    const iframe = document.createElement('iframe');
    iframe.id = SCRIPT_ID;
    iframe.title = 'Zuma AI Leasing Chatbot';
    iframe.src = `https://prod.chatbot.getzuma.com/?fullUrl=${fullUrl}`;
    iframe.style.cssText = `position:fixed;bottom:0;${position === 'left' ? 'left:0' : 'right:0'};width:400px;height:600px;border:none;z-index:9999;`;
    iframe.allow = 'microphone';
    iframe.setAttribute('sandbox', 'allow-scripts allow-same-origin allow-forms allow-popups');
    document.body.appendChild(iframe);

    return () => {
      mountedRef.current = false;
      const el = document.getElementById(SCRIPT_ID);
      if (el) el.remove();
    };
  }, [position]);

  return null;
}

const contentSchema: ComponentContentSchema = {
  slot: 'ZumaChatbot',
  displayName: 'Zuma Chatbot',
  tokens: {
    position: {
      label: 'Position',
      type: 'text',
      defaultValue: 'left',
      description: 'Side of the screen: "left" or "right".',
    },
  },
};

export const zumaChatbotBlock: BlockDefinition = {
  id: 'zuma-chatbot',
  name: 'Zuma Chatbot',
  description: 'AI leasing chatbot powered by Zuma. Automatically identifies the property by page URL.',
  category: 'Integrations',
  placement: 'injection',
  defaultScope: 'template',
  component: ZumaChatbotComponent,
  contentSchema,
  tags: ['zuma', 'chatbot', 'ai', 'leasing', 'chat'],
};
